/**
 * LoopInspector — Phase 6b-4.
 *
 * Loop / for-each over a collection. Source schema: soarNodes.js:2424.
 *
 *   collection      expression    iterable to walk (template-aware)
 *   itemVariable    string        per-iteration item binding (default 'item')
 *   indexVariable   string        per-iteration index binding (optional)
 *   maxIterations   number        safety cap (default 1000)
 *
 * Runtime semantics note (per soarNodes:2429-2433): the current runtime
 * evaluates the loop CONDITION once and routes body/done. True multi-
 * iteration re-execution is a post-launch enhancement; the Inspector
 * surfaces both fields anyway so workflows authored today round-trip
 * cleanly once the runtime catches up.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React from 'react';

export default function LoopInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const collection    = cfg.collection || '';
  const itemVariable  = cfg.itemVariable || '';
  const indexVariable = cfg.indexVariable || '';
  const maxIterations = cfg.maxIterations ?? '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Collection"
        hint="Expression that resolves to an array. Supports templates: ${upstream.items}"
      >
        <input
          type="text"
          value={collection}
          onChange={(e) => setField('collection', e.target.value)}
          placeholder="${upstream_node.result.items}"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
        />
      </FieldGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldGroup label="Item Variable" hint="Default: item">
          <input
            type="text"
            value={itemVariable}
            onChange={(e) => setField('itemVariable', e.target.value)}
            placeholder="item"
            disabled={readOnly}
            autoComplete="off"
            spellCheck={false}
            style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
          />
        </FieldGroup>

        <FieldGroup label="Index Variable" hint="Optional. e.g. index">
          <input
            type="text"
            value={indexVariable}
            onChange={(e) => setField('indexVariable', e.target.value)}
            placeholder="index"
            disabled={readOnly}
            autoComplete="off"
            spellCheck={false}
            style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
          />
        </FieldGroup>
      </div>

      <FieldGroup
        label="Max Iterations"
        hint="Safety cap. Default 1000. The loop terminates when the cap is hit."
      >
        <input
          type="number"
          min="1"
          step="1"
          value={maxIterations}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') return setField('maxIterations', undefined);
            const n = Number(v);
            if (Number.isFinite(n)) setField('maxIterations', n);
          }}
          placeholder="1000"
          disabled={readOnly}
          style={inputStyle}
        />
      </FieldGroup>

      <div style={{
        padding: 10,
        background: '#fffbeb',
        border: '0.5px solid #fcd34d',
        borderRadius: 6,
        fontSize: 11, color: '#78350f', lineHeight: 1.5,
      }}>
        <strong>Pre-launch semantics:</strong> the runtime currently evaluates
        the condition once and routes to <code style={codeChip}>body</code>{' '}
        (truthy) or <code style={codeChip}>done</code> (falsy). True
        multi-iteration re-execution is a post-launch enhancement — the fields
        above persist and will activate without re-authoring once the runtime
        upgrades.
      </div>
    </div>
  );
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const codeChip = {
  padding: '0 4px',
  borderRadius: 3,
  background: '#fef3c7',
  color: '#78350f',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
};
